import { Sparkles, StopCircle } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { graphql, useFragment, useMutation } from "react-relay";
import { Link } from "react-router";
import JobRunDetails from "src/components/JobRunDetails";
import type { GeneratePromptFeedbackMutation as GeneratePromptFeedbackMutationType } from "src/components/PromptFeedback/__generated__/GeneratePromptFeedbackMutation.graphql";
import { GeneratePromptFeedbackMutation } from "src/components/PromptFeedback/GeneratePromptFeedbackMutation";
import { ScopeGate } from "src/components/ScopeGate";
import { Button } from "src/components/ui/button";
import { UIConfirmationDialog } from "src/components/ui/UIConfirmationDialog";
import { useEmbeddedChat } from "src/context/EmbeddedChatContext";
import { useFeatureFlag } from "src/hooks/use-feature-flag";
import { useToast } from "src/hooks/use-toast";
import AskTierZero from "src/pages/Dagvestigations/AskTierZero";
import { SectionProgressType } from "src/pages/DigestPage/DigestDetails";
import type { StopJobRunMutation as StopJobRunMutationType } from "src/pages/DigestPage/mutations/__generated__/StopJobRunMutation.graphql";
import { StopJobRunMutation } from "src/pages/DigestPage/mutations/StopJobRunMutation";
import { getDigestInputSections } from "src/pages/DigestPage/utils/digestInputSections";

import { DigestJobDetails_jobRun$key } from "./__generated__/DigestJobDetails_jobRun.graphql";
import DigestSectionAccordion from "./DigestSectionAccordion";
import DigestSectionProgressBar from "./DigestSectionProgressBar";

export const digestJobDetailsFragment = graphql`
  fragment DigestJobDetails_jobRun on GraphQLJobRun {
    id
    name
    status
    inputJson
    digestSections {
      isComplete
    }
    job {
      id
      name
      latestPromptFeedback {
        id
        suggestions {
          name
          isStale
        }
      }
    }
    ...DigestSectionAccordion_jobRun
    ...JobRunDetails_jobRun
  }
`;

interface DigestJobDetailsProps {
  jobRun: DigestJobDetails_jobRun$key;
  sectionProgress: SectionProgressType | null;
}

export default function DigestJobDetails({
  jobRun,
  sectionProgress,
}: DigestJobDetailsProps) {
  const data = useFragment(digestJobDetailsFragment, jobRun);
  const { toast } = useToast();
  const { registerContext, unregisterContext } = useEmbeddedChat();
  const isRunbookFeedbackEnabled = useFeatureFlag("DIGEST_RUNBOOK_FEEDBACK");
  const [stopJobRun, isStopInFlight] = useMutation<StopJobRunMutationType>(StopJobRunMutation);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);

  const [commitGenerateFeedback, isGenerating] =
    useMutation<GeneratePromptFeedbackMutationType>(GeneratePromptFeedbackMutation);

  const handleConfirmCancel = () => {
    stopJobRun({
      variables: { input: { jobRunId: data.id } },
      onCompleted: (response) => {
        setIsCancelDialogOpen(false);
        if (response.stopJobRun?.success) {
          toast({ title: "Digest run cancelled", description: "The digest run has been stopped.", variant: "success" });
        } else {
          toast({ title: "Error", description: "Failed to cancel the digest run. Please try again.", variant: "error" });
        }
      },
      onError: (error) => {
        setIsCancelDialogOpen(false);
        toast({ title: "Error", description: error.message || "An unexpected error occurred", variant: "error" });
      },
    });
  };

  useEffect(() => {
    if (data?.id) {
      const contextString = `The user is viewing a digest report. Use the jobRunId to fetch details if needed.
Respond to the user's questions about this digest.`;

      registerContext("digest", {
        contextString,
        data: { jobRunId: data.id, type: "digest" },
      });
    }

    return () => {
      unregisterContext("digest");
    };
  }, [data?.id, registerContext, unregisterContext]);

  const inputSections = getDigestInputSections(data?.inputJson);
  const isInProgress = data?.status === "IN_PROGRESS";
  const isStopped = data?.status === "STOPPED";

  const resolvedProgress = useMemo(() => {
    if (!data || !isInProgress || inputSections.length === 0) return null;
    if (sectionProgress) return sectionProgress;
    const completedCount = data.digestSections.filter((s) => s.isComplete).length;
    return {
      activeSectionIndex: completedCount,
      totalSections: inputSections.length,
      sectionName: inputSections[completedCount]?.section ?? "",
    };
  }, [data, isInProgress, inputSections, sectionProgress]);

  if (!data) {
    return <div className="p-4 text-foreground">Loading digest...</div>;
  }

  const allSuggestions = data.job?.latestPromptFeedback?.suggestions ?? [];
  const applicableSuggestionCount = allSuggestions.filter((s) => !s.isStale).length;

  const isCompleted = data.status === "COMPLETED";

  const handleGenerateSuggestions = () => {
    commitGenerateFeedback({
      variables: { input: { jobRunId: data.id } },
      onCompleted: (response) => {
        if (response.generatePromptFeedback?.error) {
          toast({
            title: "Error",
            description: response.generatePromptFeedback.error.message,
            variant: "error",
          });
          return;
        }
        toast({ title: "Prompt suggestions generated", description: "Open Prompt Studio to review them." });
      },
      onError: (error) => {
        toast({ title: "Error", description: error.message, variant: "error" });
      },
    });
  };

  const studioUrl = `/studio?digestJobId=${data.job?.id}&suggestion=true`;

  return (
    <div>
      <JobRunDetails
        jobRun={data}
        context="digest"
        rightContent={
          <div className="flex items-center gap-2">
            {isInProgress && (
              <ScopeGate scope="write:digests">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsCancelDialogOpen(true)}
                  className="text-red-400 hover:text-red-300 hover:border-red-400"
                >
                  <StopCircle className="w-4 h-4 mr-1" />
                  Cancel
                </Button>
              </ScopeGate>
            )}
            <ScopeGate scope="write:chat">
              <AskTierZero />
            </ScopeGate>
          </div>
        }
      />

      {resolvedProgress && (
        <DigestSectionProgressBar sectionProgress={resolvedProgress} />
      )}

      <DigestSectionAccordion
        jobRun={data}
        isInProgress={isInProgress}
        isStopped={isStopped}
        sectionProgress={isInProgress ? sectionProgress : null}
        inputSections={inputSections}
      />

      {/* Prompt feedback discovery banner */}
      {isRunbookFeedbackEnabled && isCompleted && (
        <ScopeGate scope="write:digests">
          <div className="mt-6 border border-border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium text-foreground">
                  Prompt Suggestions
                </span>
                <span className="text-xs text-muted-foreground">
                  {applicableSuggestionCount > 0
                    ? `${applicableSuggestionCount} suggestion${applicableSuggestionCount !== 1 ? "s" : ""} to improve your prompts`
                    : "Get AI-powered suggestions to improve your section prompts"}
                </span>
              </div>
              {applicableSuggestionCount > 0 ? (
                <Button variant="outline" size="sm" asChild>
                  <Link to={studioUrl}>Review in Prompt Studio</Link>
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleGenerateSuggestions}
                  disabled={isGenerating}
                >
                  {isGenerating ? "Generating..." : "Get Suggestions"}
                </Button>
              )}
            </div>
          </div>
        </ScopeGate>
      )}

      <UIConfirmationDialog
        isOpen={isCancelDialogOpen}
        onClose={() => setIsCancelDialogOpen(false)}
        onConfirm={handleConfirmCancel}
        title="Cancel digest run"
        description="Are you sure you want to cancel this digest run? Any completed sections will be preserved, but remaining sections will not be processed."
        confirmText={isStopInFlight ? "Cancelling..." : "Cancel run"}
        cancelText="Keep running"
        variant="destructive"
        isLoading={isStopInFlight}
      />
    </div>
  );
}
